// The CMP consent screen owns the viewport on a first visit (no choice stored
// yet): the softwall must not stack under it. See ADR-002.
//
// Timing measured on staging: `__cmp` exists at ~200ms but the screen only
// shows at ~5.4s, right after the `settings` event. The softwall can arrive on
// either side of that, so the screen events are tracked from module load and
// the state is read at the moment a banner asks for it.

// `consentExists` is true before any interaction (CMP writes a record on load);
// `userChoiceExists` is the one that means "the visitor answered".
const hasUserChoice = (status) =>
  !!status &&
  (status.userChoiceExists === true ||
    (typeof status.userChoiceExists === 'undefined' &&
      status.consentExists &&
      status.consentData))

// Failsafe for a CMP that never decides (settings request lost): release the
// banner, but only while no screen is up.
const DECISION_TIMEOUT_MS = 15000

let screenOpen = false
// True once CMP decided whether to show the screen (loadShowing/loadNotShowing).
let decided = false
let subscribed = false
const waiting = []

// The consent layer lives in a shadow root and is kept in the DOM (display:none)
// after closing. Covers the case where it opened before this module ran.
const isScreenInDom = () => {
  const wrapper = document.getElementById('cmpwrapper')
  const root = (wrapper && wrapper.shadowRoot) || wrapper
  const box = root && root.querySelector('#cmpbox')
  return !!box && box.getBoundingClientRect().height > 0
}

const isScreenUp = () => screenOpen || isScreenInDom()

const release = () => {
  while (waiting.length) {
    waiting.shift()()
  }
}

const subscribe = () => {
  if (subscribed || typeof window.__cmp !== 'function') return
  subscribed = true

  const onOpen = () => {
    decided = true
    screenOpen = true
  }
  const onClose = () => {
    decided = true
    screenOpen = false
    release()
  }

  ;['consentscreen', 'loadShowing'].forEach((name) =>
    window.__cmp('addEventListener', [name, onOpen, false], null)
  )
  ;['consentscreenoff', 'loadNotShowing'].forEach((name) =>
    window.__cmp('addEventListener', [name, onClose, false], null)
  )
}

// Subscribe as early as the bundle runs, so the events are not missed by a
// banner that shows up minutes later.
subscribe()

// Resolves once the CMP consent screen is out of the way: immediately when the
// integration is off, when no screen will be shown, or when the visitor already
// answered; otherwise when the screen closes.
export function whenCmpScreenClosed() {
  return new Promise((resolve) => {
    if (
      !window.lgcCmpBanner ||
      window.cmp_noscreen ||
      typeof window.__cmp !== 'function'
    ) {
      resolve()
      return
    }

    subscribe()

    window.__cmp('consentStatus', null, (status) => {
      if (hasUserChoice(status)) {
        resolve()
        return
      }

      // Decided and nothing on screen: geo rules, or a screen already closed.
      if (decided && !isScreenUp()) {
        resolve()
        return
      }

      let done = false
      const settle = () => {
        if (done) return
        done = true
        const i = waiting.indexOf(settle)
        if (i !== -1) waiting.splice(i, 1)
        resolve()
      }
      waiting.push(settle)

      // No screen yet and none coming: let the banner through. A screen that is
      // up (or that opens later) keeps its waiter until `consentscreenoff`.
      window.setTimeout(() => {
        if (!isScreenUp()) settle()
      }, DECISION_TIMEOUT_MS)
    })
  })
}
